/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: organization.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { type RequestOptions, type Transport } from '../transport';

import type {
  AddUserToOrganizationRequest,
  AddUserToOrganizationResponse,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  DeleteOrganizationInvitationRequest,
  DeleteOrganizationInvitationResponse,
  DeleteOrganizationRequest,
  DeleteOrganizationResponse,
  GetCurrentUserOrganizationsRequest,
  GetCurrentUserOrganizationsResponse,
  GetOrganizationInvitationRequest,
  GetOrganizationInvitationResponse,
  GetOrganizationMembersRequest,
  GetOrganizationMembersResponse,
  GetOrganizationRequest,
  GetOrganizationResponse,
  GetOrganizationsRequest,
  GetOrganizationsResponse,
  InviteUserToOrganizationRequest,
  InviteUserToOrganizationResponse,
  ListOrganizationInvitationsRequest,
  ListOrganizationInvitationsResponse,
  RemoveUserFromOrganizationRequest,
  RemoveUserFromOrganizationResponse,
  UpdateOrganizationRequest,
  UpdateOrganizationResponse,
} from '../types/organization';

export const createOrganizationService = (transport: Transport) => ({
  async getCurrentUserOrganizations(
    request: GetCurrentUserOrganizationsRequest,
    options: RequestOptions = {},
  ): Promise<GetCurrentUserOrganizationsResponse> {
    const url = getApiPath(API_METHODS.OrganizationsService.GetCurrentUserOrganizations);
    return transport.get<GetCurrentUserOrganizationsResponse>(url, { params: request, ...options });
  },

  async getOrganizations(
    request: GetOrganizationsRequest,
    options: RequestOptions = {},
  ): Promise<GetOrganizationsResponse> {
    const url = getApiPath(API_METHODS.OrganizationsService.GetOrganizations);
    return transport.get<GetOrganizationsResponse>(url, { params: request, ...options });
  },

  async getOrganization(
    request: GetOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<GetOrganizationResponse> {
    const { organization_id } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.GetOrganization, { organization_id });
    return transport.get<GetOrganizationResponse>(url, options);
  },

  async createOrganization(
    request: CreateOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<CreateOrganizationResponse> {
    const url = getApiPath(API_METHODS.OrganizationsService.CreateOrganization);
    return transport.post<CreateOrganizationResponse>(url, request, options);
  },

  async updateOrganization(
    request: UpdateOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<UpdateOrganizationResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.UpdateOrganization, {
      organization_id,
    });
    return transport.post<UpdateOrganizationResponse>(url, body, options);
  },

  async deleteOrganization(
    request: DeleteOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<DeleteOrganizationResponse> {
    const { organization_id } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.DeleteOrganization, {
      organization_id,
    });
    return transport.delete<DeleteOrganizationResponse>(url, options);
  },

  async getOrganizationMembers(
    request: GetOrganizationMembersRequest,
    options: RequestOptions = {},
  ): Promise<GetOrganizationMembersResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.GetOrganizationMembers, {
      organization_id,
    });
    return transport.get<GetOrganizationMembersResponse>(url, { params, ...options });
  },

  async addUserToOrganization(
    request: AddUserToOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<AddUserToOrganizationResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.AddUserToOrganization, {
      organization_id,
    });
    return transport.post<AddUserToOrganizationResponse>(url, body, options);
  },

  async removeUserFromOrganization(
    request: RemoveUserFromOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<RemoveUserFromOrganizationResponse> {
    const { organization_id, user_id } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.RemoveUserFromOrganization, {
      organization_id,
      user_id,
    });
    return transport.delete<RemoveUserFromOrganizationResponse>(url, options);
  },

  async inviteUserToOrganization(
    request: InviteUserToOrganizationRequest,
    options: RequestOptions = {},
  ): Promise<InviteUserToOrganizationResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.InviteUserToOrganization, {
      organization_id,
    });
    return transport.post<InviteUserToOrganizationResponse>(url, body, options);
  },

  async listOrganizationInvitations(
    request: ListOrganizationInvitationsRequest,
    options: RequestOptions = {},
  ): Promise<ListOrganizationInvitationsResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.ListOrganizationInvitations, {
      organization_id,
    });
    return transport.get<ListOrganizationInvitationsResponse>(url, { params, ...options });
  },

  async getOrganizationInvitation(
    request: GetOrganizationInvitationRequest,
    options: RequestOptions = {},
  ): Promise<GetOrganizationInvitationResponse> {
    const { organization_id, invitation_id } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.GetOrganizationInvitation, {
      organization_id,
      invitation_id,
    });
    return transport.get<GetOrganizationInvitationResponse>(url, options);
  },

  async deleteOrganizationInvitation(
    request: DeleteOrganizationInvitationRequest,
    options: RequestOptions = {},
  ): Promise<DeleteOrganizationInvitationResponse> {
    const { organization_id, invitation_id } = request;
    const url = getApiPath(API_METHODS.OrganizationsService.DeleteOrganizationInvitation, {
      organization_id,
      invitation_id,
    });
    return transport.delete<DeleteOrganizationInvitationResponse>(url, options);
  },
});

export type OrganizationService = ReturnType<typeof createOrganizationService>;
